/**
 * The phone's widgets, from the page (plan.md step 79; the user, 2026-10-28).
 *
 * In the MyRave app, for a rave this SIGNED-IN account ATTENDS, the rave page
 * hands the shell a `WidgetPlan` built from what the app holds offline — the
 * stored bundle (every set left, on every stage), the saved sets as My lineup
 * shows them (an offline save not yet synced included), the stored forecast
 * with its age, and the map once the rave's offline download holds it — the
 * app's own offline rules, so a widget never disagrees with the app. The
 * shell writes what the widgets draw (`mobile/src/widgetShell.ts`).
 *
 * Signed out, or the rave not attended, nothing is sent; a rave the account
 * stops attending is taken out (`widget.clear`). Each rave is its own site, so
 * a rave's widget data is sent when that rave's page is open — like the
 * always-on card. A browser has no widgets: nothing is ever posted there.
 */
import { useEffect, useMemo, useRef } from 'react';
import {
  MAX_WIDGET_ACTS, MAX_WIDGET_DAYS, MAX_WIDGET_HOURS, MAX_WIDGET_POIS, applyStageDelays, directionOf, localizeBundle, localized, widgetTracks,
  type EditionBundle, type LiveCardMode, type Locale, type MapConfig, type RaveWeather, type WidgetPlan, type WidgetWords,
} from '@myrave/shared';
import { api } from '@/lib/api';
import { festivalUrl } from '@/lib/tenant';
import { refreshBundle } from '@/offline/bundle';
import { useAuth } from '@/app/AuthProvider';
import { useEdition } from '@/app/EditionProvider';
import { useLocale } from '@/app/LocaleProvider';
import { translate } from '@/i18n';
import { inNativeApp, keptLiveCard, postWidgetPlan, clearWidgets } from '@/lib/nativeBridge';
import { useOffline } from '@/offline/OfflineProvider';
import { GATES_STAGE_ID, cardActs, cardPlanFrom, useAttendsRave, type CardPlanInput } from './liveCard';
import { useLastStage, useStageAlertSetting } from './proximity';
import { useRaveWeather } from './raveWeather';

/** The widgets' words in the reader's language — every key of `WIDGET_WORDS`. */
export function widgetWords(locale: Locale): WidgetWords {
  const tr = (english: string) => translate(locale, english);
  return {
    signIn: tr('Sign in to use MyRave widgets'),
    attend: tr('Attend a rave to see it here'),
    thankYou: tr('Thank you — see you next time'),
    allStages: tr('All stages'),
    forecast: tr('Forecast‖widget'),
    typical: tr('Typical‖widget'),
    noMoreSets: tr('No more sets'),
    noSaved: tr('No saved sets yet'),
    markFrom: tr('Location from {clock}'),
    noLocation: tr('No location'),
    outsideMap: tr("You're outside the map"),
    gatesOpenIn: tr('Gates open in'),
    switchStage: tr('Show the next stage'),
    liveNow: tr('Live now‖widget'),
    signInButton: tr('Sign in'),
    findRave: tr('Find a rave'),
    noMap: tr('No map for this rave'),
    countdownDays: tr('{days}d {hours}h'),
    forecastFrom: tr('Forecast from {date}, {time}'),
    dir: directionOf(locale),
    locale,
  };
}

export interface WidgetPlanInput extends CardPlanInput {
  /** The stages' map pins — `mapPointId` names one of the map's points. */
  stages: readonly (CardPlanInput["stages"][number] & { mapPointId?: string | null; trackId?: string | null })[];
  /** The rave's categories (tracks), in the reader's words — the lists group by them, Music first. */
  tracks?: readonly { id: string; slug: string; label: string; icon?: string | null; sortOrder: number }[];
  /** The whole map, only once the offline download holds it (else null: no Live map yet). */
  fullMap: MapConfig | null;
  /** The rave's weather as the page keeps it (step 65), with its days and its age. */
  raveWeather: RaveWeather | null;
}

/** The widget plan from the stored bundle: pure, so it is tested without a phone. */
export function widgetPlanFrom(input: WidgetPlanInput): WidgetPlan {
  const locale = input.locale ?? 'en';
  // The card's own plan gives the stages' places, the logo, the cover, the address and the card's words.
  const card = cardPlanFrom({ ...input, weather: input.raveWeather ? { units: input.raveWeather.units, hours: input.raveWeather.hours } : null });
  const t = input.now.getTime();
  const pins = new Map((input.fullMap?.points ?? input.map?.points ?? []).map((p) => [p.id, p]));
  const stages = card.stages.filter((s) => s.id !== GATES_STAGE_ID).map((s) => {
    const from = input.stages.find((x) => x.id === s.id);
    const pin = from?.mapPointId;
    const p = pin ? pins.get(pin) : undefined;
    return { ...s, x: p?.x ?? null, y: p?.y ?? null, ...(from?.trackId ? { trackId: from.trackId } : {}) };
  });
  const tracks = input.tracks ? widgetTracks(input.tracks, stages) : [];
  const w = input.raveWeather;
  const map = input.fullMap;
  return {
    editionId: card.editionId,
    rave: card.rave,
    festivalName: input.festivalName || input.edition.name,
    timezone: card.timezone,
    gatesAt: input.edition.gatesOpenAt ? new Date(input.edition.gatesOpenAt).toISOString() : null,
    endsAt: card.endsAt,
    ...(card.url ? { url: card.url } : {}),
    ...(card.logoUrl ? { logoUrl: card.logoUrl } : {}),
    ...(card.coverUrl ? { coverUrl: card.coverUrl } : {}),
    ...(card.lat != null && card.lng != null ? { lat: card.lat, lng: card.lng } : {}),
    mode: card.mode,
    stageId: card.stageId,
    radiusM: card.radiusM,
    stages,
    ...(tracks.length ? { tracks } : {}),
    // EVERY set left — the lists show any day, not only the card's next one.
    acts: cardActs(input, t, Number.POSITIVE_INFINITY, MAX_WIDGET_ACTS),
    saved: input.saved.filter((id) => !id.startsWith('gates:')),
    ...(w ? { weather: {
      units: w.units,
      fetchedAt: w.fetchedAt,
      hours: w.hours.filter((h) => Date.parse(h.at) + 3_600_000 > t).slice(0, MAX_WIDGET_HOURS).map((h) => ({ at: h.at, tempC: h.tempC, condition: h.condition })),
      days: w.days.slice(0, MAX_WIDGET_DAYS).map((d) => ({ date: d.date, source: d.source, highC: d.highC, lowC: d.lowC, condition: d.condition })),
    } } : {}),
    ...(map ? { map: {
      imageUrl: absoluteUrl(map.imageUrl),
      width: map.width,
      height: map.height,
      controlPoints: map.controlPoints.map((c) => ({ lat: c.lat, lng: c.lng, x: c.x, y: c.y })),
      points: map.points.slice(0, MAX_WIDGET_POIS).map((p) => ({ id: p.id, name: p.name, category: p.category ?? null, x: p.x, y: p.y })),
    } } : {}),
    ...(card.words ? { cardWords: card.words } : {}),
    words: widgetWords(locale),
  };
}

function absoluteUrl(url: string): string {
  try { return new URL(url, typeof location === 'undefined' ? undefined : location.href).href; } catch { return url; }
}

/**
 * Mounted by `FestivalApp` for the whole rave; does nothing outside the
 * MyRave app, signed out, or for a rave this account does not attend.
 */
export function useWidgets(enabled: boolean, saved: readonly string[], now: Date): void {
  const native = useMemo(inNativeApp, []);
  const { user } = useAuth();
  const { bundle, acts, stages, tracks } = useEdition();
  const edition = bundle.edition;
  const editionId = edition.id;
  const { locale } = useLocale();
  const alerts = useStageAlertSetting();
  const lastStage = useLastStage(editionId);
  const attends = useAttendsRave(editionId);
  const { download } = useOffline();
  const { weather } = useRaveWeather(editionId, bundle);
  const mapReady = Boolean(download?.finished && !download.stalled && bundle.map);
  const savedKey = saved.join(',');
  // The sets left change at most by the hour here — the shell drops ended ones itself.
  const hour = Math.floor(now.getTime() / 3_600_000);
  const mode: LiveCardMode = keptLiveCard().mode;
  const want = enabled && native && Boolean(user) && attends === true && Boolean(edition.endsAt);

  const plan = useMemo(() => (want ? widgetPlanFrom({
    edition, artists: bundle.artists, festivalLogoUrl: bundle.festival?.logoUrl ?? null, festivalName: bundle.festival?.name ?? null,
    festivalCoverUrl: bundle.festival?.coverUrl ?? null, map: bundle.map, stages, tracks, acts,
    saved: savedKey ? savedKey.split(',') : [], stageId: lastStage, mode, radiusM: alerts.radiusM,
    now: new Date(hour * 3_600_000),
    url: typeof location === 'undefined' ? null : `${location.origin}/${encodeURIComponent(edition.slug)}`,
    locale,
    fullMap: mapReady ? bundle.map ?? null : null,
    raveWeather: weather,
  }) : null), [want, edition, bundle.artists, bundle.festival, bundle.map, stages, tracks, acts, savedKey, lastStage, mode, alerts.radiusM, hour, locale, mapReady, weather]);

  const told = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !native || attends === null) return;
    if (plan) {
      const json = JSON.stringify(plan);
      if (told.current !== json) { postWidgetPlan(plan); told.current = json; }
    } else if (user && attends === false && told.current !== 'clear') {
      // Not (or no longer) attended: out of every widget.
      clearWidgets(editionId);
      told.current = 'clear';
    }
  }, [enabled, native, plan, attends, user, editionId]);
}

/* ------------------------------------------------- From My raves (#6) --- */

/**
 * A widget plan from a stored rave bundle, as `EditionProvider` derives its
 * sets (the reader's words, the rave's stage delays) — for My raves, which has
 * no rave screen open. `source: 'list'`: the phone keeps the rave page's map,
 * stage and "follow me" over it (`mergeFromList` in the shell).
 */
export function widgetPlanFromBundle(bundle: EditionBundle, o: {
  savedSlugs: readonly string[]; now: Date; locale: Locale; url: string | null;
}): WidgetPlan {
  const view = localizeBundle(bundle, o.locale);
  const shifted = applyStageDelays(bundle.acts, bundle.delays ?? []);
  const acts = bundle.acts.map((a) => {
    const moved = shifted.get(a.id);
    return {
      id: a.id, ...(a.slug ? { slug: a.slug } : {}), displayName: localized(a.name, a.nameLocal, o.locale), stageId: a.stageId,
      startAtDate: moved?.startAt ?? new Date(a.startAt), endAtDate: moved?.endAt ?? new Date(a.endAt),
      ...(a.photoUrl ? { photoUrl: a.photoUrl } : {}),
      ...(a.artistId ? { artistId: a.artistId } : {}),
      ...(a.facilitatorId ? { facilitatorId: a.facilitatorId } : {}),
    };
  });
  const idOf = new Map(acts.flatMap((a) => (a.slug ? [[a.slug, a.id] as const] : [])));
  return {
    ...widgetPlanFrom({
      edition: view.edition, artists: bundle.artists, festivalLogoUrl: view.festival?.logoUrl ?? null,
      festivalName: view.festival?.name ?? null, festivalCoverUrl: view.festival?.coverUrl ?? null,
      map: bundle.map, stages: view.stages, tracks: view.tracks, acts,
      saved: o.savedSlugs.flatMap((slug) => { const id = idOf.get(slug); return id ? [id] : []; }),
      stageId: null, mode: 'last-stage', radiusM: 100, now: o.now, url: o.url, locale: o.locale,
      fullMap: null, raveWeather: bundle.weather ?? null,
    }),
    source: 'list',
  };
}

/** How long after its end a rave is still sent from My raves — its thank-you card. */
export const LIST_KEEPS_ENDED_MS = 7 * 24 * 3_600_000;

export interface ListedRave { editionId: string; subdomain: string; editionSlug: string; endsAt?: string | null; attending?: boolean }

/**
 * Mounted by My raves: in the phone app, signed in, every ATTENDED rave (and
 * one that ended in the last week) is read the app's own way — the stored
 * bundle first, the network behind it, kept under the account — with its
 * saved sets from the server, and handed to the widgets. A rave whose saves
 * cannot be read is skipped, never sent with none. Once per rave per page.
 */
export function useWidgetsForMyRaves(raves: readonly ListedRave[]): void {
  const native = useMemo(inNativeApp, []);
  const { user } = useAuth();
  const { locale } = useLocale();
  const sent = useRef(new Set<string>());
  const key = raves.map((r) => `${r.editionId}:${r.attending !== false}`).join(',');
  useEffect(() => {
    if (!native || !user) return;
    let on = true;
    const now = Date.now();
    const due = raves.filter((r) => r.attending !== false && !sent.current.has(r.editionId)
      && (!r.endsAt || Date.parse(r.endsAt) > now - LIST_KEEPS_ENDED_MS)).slice(0, 12);
    void (async () => {
      for (const r of due) {
        if (!on) return;
        try {
          const [{ bundle }, favs] = await Promise.all([
            refreshBundle(r.editionId),
            api.get<{ actSlugs?: string[] }>(`/editions/${r.editionId}/favorites`),
          ]);
          if (!on || !bundle.edition.endsAt) continue;
          postWidgetPlan(widgetPlanFromBundle(bundle, {
            savedSlugs: favs.actSlugs ?? [], now: new Date(now), locale,
            url: festivalUrl(r.subdomain, r.editionSlug),
          }));
          sent.current.add(r.editionId);
        } catch {
          // No answer and nothing stored, or the saves unreadable: tried again next time My raves opens.
        }
      }
    })();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, user, key, locale]);
}
